import React, { Suspense, lazy } from "react";
import { BrowserRouter, Route, Routes, Link } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import "./App.css";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import Analytics from "./components/Analytics";
import Seo from "./components/Seo";
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import { SiteProvider } from "./components/SiteContext";
import { Loading, Section } from "./components/shared";
import Home from "./pages/Home";
import DestinationList from "./pages/DestinationList";
import DestinationDetail from "./pages/DestinationDetail";
import Planner from "./pages/Planner";
import {
  Experiences, Journeys, TravelStyles, Collections, VisaList, Blog,
} from "./pages/Listings";
import {
  ExperienceDetail, JourneyDetail, TravelStyleDetail, CollectionDetail, VisaDetail,
  BlogPost, StaticPage,
} from "./pages/Details";

const AdminApp = lazy(() => import("./admin/AdminApp"));

function NotFound() {
  return (
    <Section testId="not-found">
      <Seo title="Page Not Found | Azulo Travel" description="Find your way back to Azulo Travel destinations, experiences and private journeys." noIndex />
      <div className="py-20 text-center">
        <p className="eyebrow mb-4">404</p>
        <h1 className="font-display text-4xl text-[var(--ink)] sm:text-5xl">
          This page has wandered off
        </h1>
        <p className="mx-auto mt-4 max-w-md text-sm text-[var(--muted-ink)]">
          The page you were looking for is not here. Let us point you somewhere better.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            data-testid="notfound-home-link"
            className="rounded-xl bg-[var(--brass)] px-6 py-3.5 text-sm font-semibold text-white"
          >
            Back to home
          </Link>
          <Link
            to="/plan-my-journey"
            className="rounded-xl border border-[var(--line-strong)] bg-[var(--surface)] px-6 py-3.5 text-sm font-semibold text-[var(--ink)]"
          >
            Plan a journey
          </Link>
        </div>
      </div>
    </Section>
  );
}

function PublicShell({ children }) {
  return (
    <div className="App">
      <Nav />
      <main>{children}</main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

const page = (el) => <PublicShell>{el}</PublicShell>;

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <SiteProvider>
          <ScrollToTop />
          <Analytics />
          <Toaster position="top-center" richColors closeButton />
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/admin/*" element={<AdminApp />} />

              <Route path="/" element={page(<Home />)} />
              <Route path="/international" element={page(<DestinationList region="international" />)} />
              <Route path="/international/:slug" element={page(<DestinationDetail />)} />
              <Route path="/india" element={page(<DestinationList region="india" />)} />
              <Route path="/india/:slug" element={page(<DestinationDetail />)} />
              <Route path="/experiences" element={page(<Experiences />)} />
              <Route path="/experiences/:slug" element={page(<ExperienceDetail />)} />
              <Route path="/journeys" element={page(<Journeys />)} />
              <Route path="/journeys/:slug" element={page(<JourneyDetail />)} />
              <Route path="/travel-styles" element={page(<TravelStyles />)} />
              <Route path="/travel-styles/:slug" element={page(<TravelStyleDetail />)} />
              <Route path="/collections" element={page(<Collections />)} />
              <Route path="/collections/:slug" element={page(<CollectionDetail />)} />
              <Route path="/visa" element={page(<VisaList />)} />
              <Route path="/visa/:slug" element={page(<VisaDetail />)} />
              <Route path="/blog" element={page(<Blog />)} />
              <Route path="/blog/:slug" element={page(<BlogPost />)} />
              <Route path="/plan-my-journey" element={page(<Planner />)} />
              <Route path="/corporate" element={page(<StaticPage slug="corporate" corporate />)} />
              <Route path="/about" element={page(<StaticPage slug="about" />)} />
              <Route path="/contact" element={page(<StaticPage slug="contact" />)} />
              <Route path="*" element={page(<NotFound />)} />
            </Routes>
          </Suspense>
        </SiteProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
